/*
 * Books' transactions, like QuickBooks' "For review" and Xero's bank feed: pick a category right in the list (with the
 * one used last time for the same words offered first), filter by days, account, category and money in or out, split
 * one into many categories, keep a receipt with it, and make a rule from it. Bank files, a linked bank (Plaid) and
 * typed-in cash bring them in.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
  achParts,
  bankLabel,
  payeeGuess,
  surenessOf,
  type BooksSetup,
  type CategoryRow,
  type TransactionRow,
} from "@steward/shared";
import { CategoryPick } from "./CategoryPick";
import {
  useAiReview,
  useAddReceipt,
  useAddRule,
  useBookTransactions,
  useBulkCategory,
  useChangeTransaction,
  useExcludeLines,
  useIncludeLines,
  useMatchPayments,
  useMatches,
  usePair,
  usePayout,
  usePostLines,
  useUnmatch,
  useUnpair,
  useImport,
  usePlaidConnect,
  usePlaidLinkToken,
  usePlaidSync,
  useReceipts,
  useRemoveReceipt,
  useRemoveTransaction,
  useSplitPayouts,
  useTyped,
  useUndoImport,
} from "../../api/books";
import { useSession } from "../../app/session";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { DataTable } from "../../components/DataTable";
import { Choice, Combobox, FormField, InputText } from "../../components/fields";
import { moneyExact, plural, shortDate, todayIn } from "../../components/format";
import { Icon } from "../../components/Icon";
import { FilterPick, Menu, Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, EmptyState, IconButton, StatusLabel, Switch, ago } from "../../components/parts";
import {
  KIND_WORDS,
  SOURCE_WORDS,
  SpanPicker,
  StartDayPick,
  Tile,
  base64Of,
  centsOf,
  downloadCsv,
  dollars,
  errText,
  openReceipt,
  pickList,
  useSpan,
  whole,
} from "./common";

type Way = "all" | "in" | "out";
/** Pending lines by the AI reviewer's note: all, the ones it is not sure of (check these), the ones it is sure of. */
type AiView = "all" | "check" | "sure" | "byAi";

/**
 * Lines from the same place: the company on an ACH line (every ACH line starts "ORIG CO NAME", so its first words say
 * nothing), else the first words without numbers, so "SHELL OIL 57442" and "SHELL OIL 81230" are the same place.
 */
const wordsKey = (d: string) =>
  achParts(d)?.name.toLowerCase() ??
  d
    .toLowerCase()
    .replace(/[^a-z ]+/g, " ")
    .split(" ")
    .filter((w) => w.length > 1)
    .slice(0, 2)
    .join(" ");

/** A plain select of categories, grouped like the profit and loss, for picking right in the list. */
function CategorySelect({
  categories,
  value,
  onChange,
  label,
  blank = "Pick a category",
}: {
  categories: CategoryRow[];
  value: string;
  onChange: (v: string) => void;
  label: string;
  blank?: string;
}) {
  const shown = categories.filter((c) => !c.hidden && c.type !== "open");
  const groups = [...new Set(shown.map((c) => c.group))];
  const nameOf = new Map(shown.map((c) => [c.id, c.name]));
  return (
    <select className="bk-cat-select" aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{blank}</option>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {shown
            .filter((c) => c.group === g)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.parentId ? `${nameOf.get(c.parentId) ?? ""}, ${c.name}` : c.name}
              </option>
            ))}
        </optgroup>
      ))}
    </select>
  );
}

/** Keeps a click or key inside a list cell from opening the row. */
function Stop({ children }: { children: ReactNode }) {
  return (
    <span className="bk-stop" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      {children}
    </span>
  );
}

export type ReviewState = "pending" | "posted" | "excluded";

/** The marker in front of a category: sure (green), a guess (blue), or worth a second look (orange). */
function Sure({ t, cats }: { t: TransactionRow; cats: Map<string, CategoryRow> }) {
  const s = surenessOf({
    amountCents: t.amountCents,
    description: t.description,
    categoryType: t.split ? "split" : t.categoryId ? (cats.get(t.categoryId)?.type ?? null) : null,
    auto: t.auto,
    split: Boolean(t.split),
    paired: t.pairId !== null,
    matched: t.matchedPayments.length > 0,
    reviewed: t.reviewed,
  });
  return (
    <span className={`bk-sure ${s.level}`} title={s.why} aria-label={s.why}>
      <Icon name={s.level === "sure" ? "check" : s.level === "guess" ? "info" : "warning"} size={14} />
    </span>
  );
}

/**
 * One card per account across the top, like QuickBooks' bank feed: what it is, what the bank says it holds next to what
 * Books says, how many lines wait, and when the bank last sent lines. Pressing one shows only its lines.
 */
function AccountCards({
  setup,
  accountId,
  setAccountId,
}: {
  setup: BooksSetup;
  accountId: string;
  setAccountId: (v: string) => void;
}) {
  const sync = usePlaidSync();
  const toast = useToast();
  if (setup.accounts.length === 0) return null;
  return (
    <div className="bk-acct-cards" role="list" aria-label="Accounts">
      {setup.accounts.map((a) => {
        const off = a.bankBalanceCents !== null && a.booksBalanceCents !== null ? a.bankBalanceCents - a.booksBalanceCents : null;
        const on = accountId === a.id;
        return (
          <div role="listitem" key={a.id} className={`bk-acct-card${on ? " on" : ""}`}>
            <button
              type="button"
              className="bk-acct-main"
              aria-pressed={on}
              onClick={() => setAccountId(on ? "" : a.id)}
              title={on ? "Show every account" : `Show only ${a.name}`}
            >
              <span className="bk-acct-top">
                <span className="bk-acct-name">{a.name}</span>
                {a.pending ? (
                  <span className="bk-acct-count" title={`${plural(a.pending, "line")} to review`}>
                    {a.pending}
                  </span>
                ) : (
                  <span className="bk-acct-done" title="Nothing to review">
                    <Icon name="check" size={12} />
                  </span>
                )}
              </span>
              <span className="muted small">
                {KIND_WORDS[a.kind] ?? a.kind}
                {a.last4 ? ` ending ${a.last4}` : ""}
                {a.personal ? ", personal" : ""}
              </span>
              {a.bankBalanceCents !== null ? (
                <span className="bk-acct-bal">
                  <span className="muted small">Bank</span> <b>{moneyExact(a.bankBalanceCents)}</b>
                </span>
              ) : null}
              {/* Posted, like QuickBooks: only lines posted so far. Equal to the bank once every line is posted. */}
              <span className={a.bankBalanceCents !== null ? "bk-acct-books small" : "bk-acct-bal"}>
                <span className="muted small">Posted</span>{" "}
                {a.postedBalanceCents === null ? (
                  <span className="muted small">no opening balance</span>
                ) : a.bankBalanceCents !== null ? (
                  moneyExact(a.postedBalanceCents)
                ) : (
                  <b>{moneyExact(a.postedBalanceCents)}</b>
                )}
              </span>
              {off ? (
                <span className="bk-acct-off small" title="Lines may be missing, or the opening balance is wrong">
                  Off by {moneyExact(Math.abs(off))}
                </span>
              ) : a.bankBalanceCents !== null && a.postedBalanceCents !== null ? (
                a.postedBalanceCents === a.bankBalanceCents ? (
                  <span className="bk-acct-match small">
                    <Icon name="check" size={12} /> Matches the bank
                  </span>
                ) : (
                  <span className="muted small">Matches once the {plural(a.pending, "line")} are posted</span>
                )
              ) : null}
            </button>
            {a.linked ? (
              <button
                type="button"
                className="bk-acct-sync small muted"
                title="Pull from the bank now"
                disabled={sync.isPending}
                onClick={() =>
                  void sync.mutateAsync().then(
                    (r) => toast(`${String(r.added)} new from the bank`),
                    (e: unknown) => toast(errText(e), { kind: "error" }),
                  )
                }
              >
                <Icon name="repeat" size={12} /> {a.lastSyncAt ? ago(a.lastSyncAt) : "Not yet"}
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function Transactions({
  setup,
  today,
  state,
  setState,
}: {
  setup: BooksSetup;
  today: string;
  state: ReviewState;
  setState: (v: ReviewState) => void;
}) {
  const { company } = useSession();
  const span = useSpan(today, "year");
  const [accountId, setAccountId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [way, setWay] = useState<Way>("all");
  const [aiView, setAiView] = useState<AiView>("all");
  const ai = useAiReview();
  const list = useBookTransactions(company.id, { ...span.span, accountId, categoryId, state });
  // Every row of the year (any category), so the list can offer what the same words got last time.
  const history = useBookTransactions(company.id, { from: `${String(Number(today.slice(0, 4)) - 1)}-01-01`, to: today });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkCat, setBulkCat] = useState("");
  const [open, setOpen] = useState<TransactionRow | null>(null);
  const [upload, setUpload] = useState(false);
  const [typing, setTyping] = useState(false);
  const bulk = useBulkCategory();
  const change = useChangeTransaction();
  const postLines = usePostLines();
  const exclude = useExcludeLines();
  const include = useIncludeLines();
  const split = useSplitPayouts();
  const sync = usePlaidSync();
  const toast = useToast();
  const cats = useMemo(() => new Map(setup.categories.map((c) => [c.id, c])), [setup.categories]);
  const accts = useMemo(() => new Map(setup.accounts.map((a) => [a.id, a])), [setup.accounts]);
  const lastUsed = useMemo(() => {
    const m = new Map<string, Guess>();
    for (const t of history.data?.transactions ?? []) {
      if (!t.categoryId || t.pairId || !t.reviewed) continue;
      const k = `${wordsKey(t.description)}|${t.amountCents > 0 ? "in" : "out"}`;
      if (!m.has(k)) m.set(k, { categoryId: t.categoryId, day: t.day, amountCents: t.amountCents });
    }
    return m;
  }, [history.data]);
  const guessOf = (r: TransactionRow): Guess | undefined =>
    r.categoryId || r.split ? undefined : lastUsed.get(`${wordsKey(r.description)}|${r.amountCents > 0 ? "in" : "out"}`);
  const all = list.data?.transactions ?? [];
  const rows = all
    .filter((r) => way === "all" || (way === "in" ? r.amountCents > 0 : r.amountCents < 0))
    .filter((r) =>
      state === "posted"
        ? aiView !== "byAi" || Boolean(r.ai?.posted)
        : state !== "pending" ||
          aiView === "all" ||
          aiView === "byAi" ||
          (r.ai ? r.ai.sure === (aiView === "sure") : aiView === "check"),
    );
  const aiRun = (again = false) =>
    void run(async () => {
      const a = await ai.mutateAsync({ ...(accountId ? { accountId } : {}), ...(again ? { again } : {}) });
      if (!a.reviewed && !a.paired) return "Nothing new to review. Every pending line has an AI note.";
      const more = a.left ? ` Press again for ${plural(a.left, "more line")}.` : "";
      const done = [
        a.posted ? `posted ${plural(a.posted, "line")} it was sure of` : "",
        a.paired ? `paired ${plural(a.paired, "transfer")}` : "",
      ].filter(Boolean);
      const left = a.reviewed - a.posted;
      return `The AI went over ${plural(a.reviewed, "line")}${done.length ? ` and ${done.join(" and ")}` : ""}.${
        left > 0 ? ` Check the ${plural(left, "line")} left in Pending.` : ""
      }${more}`;
    });
  const moneyIn = rows.filter((r) => r.amountCents > 0).reduce((a, r) => a + r.amountCents, 0);
  const moneyOut = rows.filter((r) => r.amountCents < 0).reduce((a, r) => a - r.amountCents, 0);
  const pendingAll = setup.accounts.reduce((a, x) => a + x.pending, 0);
  const lock = setup.lockDay;
  const catName = (t: TransactionRow) =>
    t.split ? `Split into ${String(t.split.length)}` : t.categoryId ? (cats.get(t.categoryId)?.name ?? "") : null;
  const run = async (what: () => Promise<string>) => {
    try {
      toast(await what());
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  const setCat = (t: TransactionRow, id: string) =>
    void run(async () => {
      await change.mutateAsync({ id: t.id, categoryId: id || null });
      return id ? `Posted as ${cats.get(id)?.name ?? ""}` : "Category taken off";
    });
  const tick = (what: string, r: { changed: number; skipped: number }) => {
    setPicked(new Set());
    return `${plural(r.changed, "line")} ${what}${r.skipped ? `, ${String(r.skipped)} left as they were` : ""}`;
  };
  const exportRows = () =>
    downloadCsv(`transactions-${span.span.from}-to-${span.span.to}.csv`, [
      ["Day", "Account", "What the bank says", "Paid to", "Category", "Amount", "Note", "Posted", "Reconciled"],
      ...rows.map((r) => [
        r.day,
        accts.get(r.accountId)?.name ?? "",
        r.description,
        r.payee,
        r.split
          ? r.split.map((p) => `${cats.get(p.categoryId)?.name ?? ""} ${dollars(p.amountCents)}`).join("; ")
          : (catName(r) ?? ""),
        dollars(r.amountCents),
        r.note,
        r.reviewed ? "yes" : "",
        r.reconciled ? "yes" : "",
      ]),
    ]);
  const hasCash = setup.accounts.some((a) => a.kind === "cash");
  return (
    <>
      {setup.connections
        .filter((c) => c.status !== "ok")
        .map((c) => (
          <Banner key={c.id} type="warning" action={c.status === "needs_login" ? <FixLink connectionId={c.id} /> : undefined}>
            {c.error ?? `${c.institution ?? "A linked bank"} stopped sending transactions.`}
          </Banner>
        ))}
      <AccountCards setup={setup} accountId={accountId} setAccountId={setAccountId} />
      <div className="row between bk-toolbar">
        <div className="segmented" role="tablist" aria-label="Which transactions">
          <button type="button" role="tab" aria-selected={state === "pending"} onClick={() => setState("pending")}>
            Pending{pendingAll ? ` (${String(pendingAll)})` : ""}
          </button>
          <button type="button" role="tab" aria-selected={state === "posted"} onClick={() => setState("posted")}>
            Posted
          </button>
          <button type="button" role="tab" aria-selected={state === "excluded"} onClick={() => setState("excluded")}>
            Excluded
          </button>
        </div>
        <div className="row tight">
          <Button icon="file" onClick={() => setUpload(true)}>
            Upload a bank file
          </Button>
          <LinkBank setup={setup} />
          <Menu button={({ toggle }) => <IconButton icon="more" label="More ways to add" onClick={toggle} />}>
            {(close) => (
              <>
                {setup.connections.length ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      close();
                      void run(async () => `${String((await sync.mutateAsync()).added)} new from the bank`);
                    }}
                  >
                    Pull from the bank now
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  disabled={!hasCash}
                  title={hasCash ? undefined : "Add a cash account first"}
                  onClick={() => {
                    close();
                    setTyping(true);
                  }}
                >
                  Type in cash
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    void run(
                      async () =>
                        `${String((await split.mutateAsync(Number(span.span.to.slice(0, 4)))).split)} Stripe payouts split into sales and fees`,
                    );
                  }}
                >
                  Split Stripe payouts into sales and fees
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    close();
                    exportRows();
                  }}
                >
                  Download this list (CSV)
                </button>
              </>
            )}
          </Menu>
        </div>
      </div>

      <div className="tiles">
        <Tile label="Money in" value={whole(moneyIn)} hint={plural(rows.filter((r) => r.amountCents > 0).length, "deposit")} />
        <Tile label="Money out" value={whole(moneyOut)} hint={plural(rows.filter((r) => r.amountCents < 0).length, "payment")} />
        <Tile
          label="Need a category"
          value={String(all.filter((r) => !r.categoryId && !r.split).length)}
          hint={state === "pending" ? "Not in profit until they have one" : "In this list"}
        />
      </div>

      <DataTable
        title={state === "pending" ? "To review" : state === "posted" ? "Posted" : "Left out of the books"}
        rows={rows}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        search={(r) =>
          `${r.description} ${r.payee ?? ""} ${r.note ?? ""} ${catName(r) ?? ""} ${dollars(Math.abs(r.amountCents))}`
        }
        onRowClick={setOpen}
        selected={picked}
        onSelect={setPicked}
        tools={
          <div className="row tight bk-filters">
            <SpanPicker s={span} today={today} />
            <FilterPick<string>
              label="Account"
              value={accountId}
              onChange={setAccountId}
              options={[{ value: "", label: "All" }, ...setup.accounts.map((a) => ({ value: a.id, label: a.name }))]}
            />
            <FilterPick<Way>
              label="Money"
              value={way}
              onChange={setWay}
              options={[
                { value: "all", label: "In and out" },
                { value: "in", label: "In" },
                { value: "out", label: "Out" },
              ]}
            />
            {state === "pending" ? (
              <>
                <FilterPick<AiView>
                  label="AI"
                  value={aiView}
                  onChange={setAiView}
                  options={[
                    { value: "all", label: "All" },
                    { value: "check", label: "Not sure, check these" },
                    { value: "sure", label: "Sure" },
                  ]}
                />
                <Button size="small" type="secondary" icon="sparkle" loading={ai.isPending} onClick={() => aiRun()}>
                  Review with AI
                </Button>
                <Menu button={({ toggle }) => <IconButton icon="more" label="More AI review" onClick={toggle} />}>
                  {(close) => (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        close();
                        aiRun(true);
                      }}
                    >
                      Review every pending line again
                    </button>
                  )}
                </Menu>
              </>
            ) : null}
            {state === "posted" ? (
              <FilterPick<AiView>
                label="Posted by"
                value={aiView === "byAi" ? "byAi" : "all"}
                onChange={setAiView}
                options={[
                  { value: "all", label: "Anyone" },
                  { value: "byAi", label: "The AI" },
                ]}
              />
            ) : null}
            {state === "posted" ? (
              <FilterPick<string>
                label="Category"
                value={categoryId}
                onChange={setCategoryId}
                options={[{ value: "", label: "All" }, ...pickList(setup.categories)]}
              />
            ) : null}
          </div>
        }
        bulk={
          state === "excluded" ? (
            <Button
              size="small"
              loading={include.isPending}
              onClick={() => void run(async () => tick("brought back", await include.mutateAsync([...picked])))}
            >
              Bring back {plural(picked.size, "one")}
            </Button>
          ) : (
            <div className="row tight">
              <CategorySelect
                categories={setup.categories}
                value={bulkCat}
                onChange={setBulkCat}
                label="Category for the ticked ones"
              />
              <Button
                size="small"
                disabled={!bulkCat}
                loading={bulk.isPending}
                onClick={() =>
                  void run(async () => {
                    const r = await bulk.mutateAsync({ ids: [...picked], categoryId: bulkCat });
                    setPicked(new Set());
                    return `${plural(r.changed, "transaction")} posted`;
                  })
                }
              >
                Save {plural(picked.size, "one")}
              </Button>
              {state === "pending" ? (
                <Button
                  size="small"
                  type="secondary"
                  loading={postLines.isPending}
                  onClick={() => void run(async () => tick("posted", await postLines.mutateAsync([...picked])))}
                >
                  Post as they are
                </Button>
              ) : null}
              <Button
                size="small"
                type="tertiary"
                loading={exclude.isPending}
                onClick={() => void run(async () => tick("left out", await exclude.mutateAsync([...picked])))}
              >
                Exclude
              </Button>
            </div>
          )
        }
        empty={
          state === "pending" ? (
            <EmptyState icon="check" title="All caught up">
              Every line in these days is posted.
            </EmptyState>
          ) : state === "excluded" ? (
            <EmptyState icon="books" title="Nothing left out">
              Lines you exclude (a double, a personal buy) wait here, out of the books.
            </EmptyState>
          ) : (
            <EmptyState icon="books" title="No transactions in these days" />
          )
        }
        columns={[
          { key: "d", header: "Day", sort: (r) => r.day, render: (r) => shortDate(r.day) },
          {
            key: "w",
            header: "What the bank says",
            sort: (r) => r.description,
            render: (r) => (
              <span className="bk-desc" title={r.description}>
                <b>{bankLabel(r.description)}</b>
                <span className="muted small">
                  {accts.get(r.accountId)?.name ?? ""}
                  {achParts(r.description)?.entry ? `, ${achParts(r.description)!.entry}` : ""}
                  {r.note ? `, ${r.note}` : ""}
                </span>
              </span>
            ),
          },
          {
            key: "p",
            header: "From or to",
            sort: (r) => r.payee ?? payeeGuess(r.description),
            render: (r) =>
              r.payee ??
              (payeeGuess(r.description) ? (
                <span className="muted" title="From the bank's words. Open the line to save it">
                  {payeeGuess(r.description)}
                </span>
              ) : (
                <span className="muted small">Not set</span>
              )),
          },
          {
            key: "c",
            header: "Match or category",
            sort: (r) => catName(r) ?? "",
            render: (r) => {
              const locked = lock !== null && r.day <= lock;
              if (state === "excluded") return <span className="muted">{catName(r) ?? "No category"}</span>;
              if (r.pairId || r.matchedPayments.length || r.split || locked || state === "posted")
                return (
                  <span className="bk-cat-cell">
                    <Sure t={r} cats={cats} />
                    {r.pairId ? <StatusLabel status="success">Pair</StatusLabel> : null}
                    {r.matchedPayments.length ? <StatusLabel status="success">Match</StatusLabel> : null}
                    {catName(r) ?? <StatusLabel status="warning">Needs a category</StatusLabel>}
                    {locked ? <Icon name="lock" size={12} /> : null}
                  </span>
                );
              const guess = guessOf(r);
              const found = r.pairsFound + r.paymentsFound;
              return (
                <Stop>
                  <span className="bk-cat-cell">
                    {found ? (
                      <button type="button" className="bk-found" onClick={() => setOpen(r)}>
                        <StatusLabel status="success">{r.pairsFound ? "Pair" : "Match"}</StatusLabel>
                        {r.pairsFound
                          ? r.pairsFound === 1
                            ? "Other side found"
                            : `${String(r.pairsFound)} possible pairs`
                          : r.paymentsFound === 1
                            ? "Payment found"
                            : `${String(r.paymentsFound)} possible matches`}
                      </button>
                    ) : (
                      <>
                        {r.ai && !guess ? (
                          <span
                            className={`bk-sure bk-ai${r.ai.sure ? " guess" : " check"}`}
                            title={r.ai.sure ? "The AI is sure. Post it if it looks right" : "The AI is not sure. Check it"}
                            aria-label={r.ai.sure ? "The AI is sure" : "The AI is not sure"}
                          >
                            <Icon name="sparkle" size={14} />
                          </span>
                        ) : guess ? (
                          <span
                            className="bk-sure guess"
                            title={`Used last time for ${bankLabel(r.description)}. Post to keep it`}
                            aria-label="Used last time"
                          >
                            <Icon name="info" size={14} />
                          </span>
                        ) : (
                          <Sure t={r} cats={cats} />
                        )}
                        <CategoryPick
                          categories={setup.categories}
                          value={r.categoryId ?? guess?.categoryId ?? ""}
                          guessed={Boolean(guess)}
                          onPick={(v) => setCat(r, v)}
                          label={`Category for ${bankLabel(r.description)}`}
                        />
                        {r.auto ? <span className="muted small">by a rule</span> : null}
                        {r.ai ? (
                          <span className="bk-ai-why">
                            {r.ai.posted ? <b>Posted by AI. </b> : null}
                            {r.ai.hint === "transfer" && !r.ai.posted
                              ? "Looks like a transfer between your accounts. Open it to pair it."
                              : r.ai.hint === "personal"
                                ? `Looks personal. ${r.ai.why}`
                                : r.ai.why}
                          </span>
                        ) : null}
                        {r.ai?.tax ? <span className="bk-ai-tax">{r.ai.tax}</span> : null}
                      </>
                    )}
                  </span>
                </Stop>
              );
            },
          },
          {
            key: "f",
            header: "",
            render: (r) =>
              r.receipts ? (
                <span className="bk-clip" title={plural(r.receipts, "receipt")}>
                  <Icon name="file" size={14} />
                  {r.receipts > 1 ? r.receipts : null}
                </span>
              ) : null,
          },
          {
            key: "m",
            header: "Amount",
            align: "right",
            sort: (r) => r.amountCents,
            render: (r) => (
              <span className={`num${r.amountCents > 0 ? " positive" : ""}`}>
                {moneyExact(r.amountCents)}
                {r.reconciled ? (
                  <span className="bk-rec" title="Reconciled">
                    <Icon name="check" size={12} />
                  </span>
                ) : null}
              </span>
            ),
          },
          ...(state === "posted"
            ? []
            : [
                {
                  key: "a",
                  header: "",
                  render: (r: TransactionRow) => (
                    <Stop>
                      {state === "excluded" ? (
                        <Button
                          size="small"
                          type="tertiary"
                          onClick={() => void run(async () => tick("brought back", await include.mutateAsync([r.id])))}
                        >
                          Bring back
                        </Button>
                      ) : (
                        <span className="row tight bk-actions">
                          <Button
                            size="small"
                            type="secondary"
                            disabled={!r.categoryId && !r.split && !guessOf(r)}
                            title={r.categoryId || r.split || guessOf(r) ? "Post it with this category" : "Pick a category first"}
                            onClick={() => {
                              const g = guessOf(r);
                              if (g) setCat(r, g.categoryId);
                              else void run(async () => tick("posted", await postLines.mutateAsync([r.id])));
                            }}
                          >
                            Post
                          </Button>
                          <Menu button={({ toggle }) => <IconButton icon="more" label="More for this line" onClick={toggle} />}>
                            {(close) => (
                              <>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    close();
                                    setOpen(r);
                                  }}
                                >
                                  Open, split or pair
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  disabled={r.reconciled}
                                  onClick={() => {
                                    close();
                                    void run(async () => tick("left out", await exclude.mutateAsync([r.id])));
                                  }}
                                >
                                  Exclude
                                </button>
                              </>
                            )}
                          </Menu>
                        </span>
                      )}
                    </Stop>
                  ),
                },
              ]),
        ]}
      />
      {open ? <TransactionModal setup={setup} t={open} guess={guessOf(open)} onClose={() => setOpen(null)} /> : null}
      <UploadModal setup={setup} open={upload} onClose={() => setUpload(false)} />
      <TypedModal setup={setup} open={typing} onClose={() => setTyping(false)} />
    </>
  );
}

// One transaction ----------------------------------------------------------------------------------------------------

interface Part {
  categoryId: string;
  amount: string;
}

/** Last time's category for lines from the same place, and the line it came from. */
interface Guess {
  categoryId: string;
  day: string;
  amountCents: number;
}

function TransactionModal({
  setup,
  t,
  guess,
  onClose,
}: {
  setup: BooksSetup;
  t: TransactionRow;
  guess: Guess | undefined;
  onClose: () => void;
}) {
  const { company } = useSession();
  const change = useChangeTransaction();
  const addRule = useAddRule();
  const remove = useRemoveTransaction();
  const toast = useToast();
  const closed = setup.lockDay !== null && t.day <= setup.lockDay;
  // A pair or a match sets the category; undo it to pick another.
  const tied = t.pairId !== null || t.matchedPayments.length > 0;
  const locked = closed || tied;
  const include = useIncludeLines();
  const [categoryId, setCategoryId] = useState(t.categoryId ?? guess?.categoryId ?? "");
  const [splitting, setSplitting] = useState(Boolean(t.split));
  const [parts, setParts] = useState<Part[]>(
    t.split
      ? t.split.map((p) => ({ categoryId: p.categoryId, amount: (Math.abs(p.amountCents) / 100).toFixed(2) }))
      : [
          { categoryId: t.categoryId ?? "", amount: (Math.abs(t.amountCents) / 100).toFixed(2) },
          { categoryId: "", amount: "" },
        ],
  );
  const [payee, setPayee] = useState(t.payee ?? payeeGuess(t.description));
  const [note, setNote] = useState(t.note ?? "");
  const [rule, setRule] = useState(false);
  // An ACH line's rule matches its company, not "ORIG CO" (every ACH line starts with those words).
  const [match, setMatch] = useState(achParts(t.description)?.name ?? t.description.split(/\s+/).slice(0, 2).join(" "));
  const [ruleWay, setRuleWay] = useState<"any" | "same">("same");
  const [ruleAccount, setRuleAccount] = useState(false);
  const sign = t.amountCents < 0 ? -1 : 1;
  const partCents = parts.map((p) => centsOf(p.amount || "0") ?? 0);
  const left = Math.abs(t.amountCents) - partCents.reduce((a, c) => a + c, 0);
  const splitOk = parts.length >= 2 && parts.every((p, i) => p.categoryId && partCents[i]! > 0) && left === 0;
  const save = async () => {
    try {
      if (splitting && !locked) {
        await change.mutateAsync({
          id: t.id,
          split: parts.map((p, i) => ({ categoryId: p.categoryId, amountCents: sign * partCents[i]! })),
          payee,
          note,
        });
      } else {
        await change.mutateAsync({
          id: t.id,
          ...(locked ? {} : { categoryId: categoryId || null }),
          ...(t.split && !locked ? { split: null } : {}),
          payee,
          note,
        });
      }
      if (rule && categoryId && !splitting) {
        const r = await addRule.mutateAsync({
          match,
          categoryId,
          ...(payee ? { payee } : {}),
          direction: ruleWay === "same" ? (t.amountCents > 0 ? "in" : "out") : null,
          accountId: ruleAccount ? t.accountId : null,
        });
        toast(`Saved. The rule also set ${plural(r.applied, "more transaction")}.`);
      } else toast("Saved");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  const account = setup.accounts.find((a) => a.id === t.accountId);
  if (t.excluded)
    return (
      <Modal
        title={`${moneyExact(t.amountCents)} on ${shortDate(t.day)}`}
        open
        onClose={onClose}
        footer={
          <Button
            loading={include.isPending}
            onClick={() =>
              void include.mutateAsync([t.id]).then(
                () => {
                  toast("Back in the books, waiting under Pending");
                  onClose();
                },
                (e: unknown) => toast(errText(e), { kind: "error" }),
              )
            }
          >
            Bring back
          </Button>
        }
      >
        <div className="bk-tx-head">
          <b>{t.description}</b>
          <span className="muted small">{account?.name ?? ""}</span>
        </div>
        <Banner type="notice">Left out of the books. It does not count anywhere until you bring it back.</Banner>
      </Modal>
    );
  return (
    <Modal
      title={`${moneyExact(t.amountCents)} on ${shortDate(t.day)}`}
      open
      onClose={onClose}
      footer={
        <>
          {!t.reconciled && !locked ? (
            <Button
              type="tertiary"
              variation="destructive"
              onClick={() =>
                void remove.mutateAsync(t.id).then(
                  () => {
                    toast("Left out of the books. Find it under Excluded.");
                    onClose();
                  },
                  (e: unknown) => toast(errText(e), { kind: "error" }),
                )
              }
            >
              Exclude
            </Button>
          ) : null}
          <Button onClick={() => void save()} loading={change.isPending || addRule.isPending} disabled={splitting && !splitOk}>
            Save
          </Button>
        </>
      }
    >
      <div className="bk-tx-head">
        <b>{bankLabel(t.description)}</b>
        {achParts(t.description) ? <span className="muted small bk-raw">{t.description}</span> : null}
        <span className="muted small">
          {account?.name ?? ""}, {SOURCE_WORDS[t.source] ?? t.source}
          {t.matched ? ", also in another download" : ""}
          {t.reconciled ? ", reconciled" : ""}
        </span>
      </div>
      <MatchPanel setup={setup} t={t} onDone={onClose} />
      {closed ? (
        <Banner type="notice">The books are closed through {shortDate(setup.lockDay)}. The category cannot change.</Banner>
      ) : null}
      <div className="stack">
        {!locked ? (
          <div className="segmented" role="tablist" aria-label="Category or split">
            <button type="button" role="tab" aria-selected={!splitting} onClick={() => setSplitting(false)}>
              One category
            </button>
            <button type="button" role="tab" aria-selected={splitting} onClick={() => setSplitting(true)}>
              Split
            </button>
          </div>
        ) : null}
        {!splitting ? (
          <FormField
            label="Category"
            description={
              guess && !t.categoryId && categoryId === guess.categoryId
                ? `Used last time for ${bankLabel(t.description)} (${shortDate(guess.day)}, ${moneyExact(guess.amountCents)}). Save to keep it.`
                : undefined
            }
          >
            {locked ? (
              <span>{t.categoryId ? (setup.categories.find((c) => c.id === t.categoryId)?.name ?? "") : "None"}</span>
            ) : (
              <CategoryPick
                categories={setup.categories}
                value={categoryId}
                guessed={Boolean(guess) && !t.categoryId && categoryId === guess?.categoryId}
                onPick={setCategoryId}
                label="Category"
              />
            )}
          </FormField>
        ) : (
          <div className="bk-split">
            {parts.map((p, i) => (
              <div className="bk-split-row" key={i}>
                <CategorySelect
                  categories={setup.categories}
                  value={p.categoryId}
                  label={`Category for part ${String(i + 1)}`}
                  onChange={(v) => setParts(parts.map((x, j) => (j === i ? { ...x, categoryId: v } : x)))}
                />
                <InputText
                  label={`Amount ${String(i + 1)}`}
                  prefix="$"
                  inputMode="decimal"
                  value={p.amount}
                  onChange={(e) => setParts(parts.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                />
                {parts.length > 2 ? (
                  <IconButton icon="close" label="Take this part out" onClick={() => setParts(parts.filter((_, j) => j !== i))} />
                ) : (
                  <span />
                )}
              </div>
            ))}
            <div className="row between">
              <Button
                size="small"
                type="tertiary"
                icon="plus"
                disabled={parts.length >= 20}
                onClick={() => setParts([...parts, { categoryId: "", amount: left > 0 ? (left / 100).toFixed(2) : "" }])}
              >
                Add a part
              </Button>
              <span className={left === 0 ? "muted small" : "bk-left small"}>
                {left === 0 ? "Adds up" : left > 0 ? `${moneyExact(left)} left to place` : `${moneyExact(-left)} too much`}
              </span>
            </div>
          </div>
        )}
        <InputText
          label="From or to"
          description={
            !t.payee && payee && payee === payeeGuess(t.description)
              ? "Read from the bank's words. Save to keep it."
              : "Who was paid or paid you. For 1099s and the Vendors report"
          }
          value={payee}
          maxLength={60}
          onChange={(e) => setPayee(e.target.value)}
        />
        <InputText label="Note" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        {categoryId && !splitting && !locked ? (
          <div className="bk-rule">
            <Switch checked={rule} onChange={setRule} label="Always use this category" />
            <div className="stack tight">
              <b>Always use this category</b>
              {rule ? (
                <>
                  <InputText label="When the bank says" value={match} maxLength={60} onChange={(e) => setMatch(e.target.value)} />
                  <Choice
                    label="For"
                    value={ruleWay}
                    onChange={setRuleWay}
                    options={[
                      { value: "same", label: t.amountCents > 0 ? "Money in only" : "Money out only" },
                      { value: "any", label: "Money in or out" },
                    ]}
                  />
                  <div className="row tight">
                    <Switch checked={ruleAccount} onChange={setRuleAccount} label={`Only ${account?.name ?? "this account"}`} />
                    <span>Only {account?.name ?? "this account"}</span>
                  </div>
                </>
              ) : (
                <span className="muted small">Make a rule so the next ones get this category on their own</span>
              )}
            </div>
          </div>
        ) : null}
        <Receipts transactionId={t.id} companyId={company.id} />
      </div>
    </Modal>
  );
}

/**
 * What a line could be, at the top of its window: the other side of a transfer between our accounts (Pair), the cash or
 * checks it took to the bank (Match), or a Stripe payout. A paired or matched line shows what it is tied to instead.
 */
function MatchPanel({ setup, t, onDone }: { setup: BooksSetup; t: TransactionRow; onDone: () => void }) {
  const locked = setup.lockDay !== null && t.day <= setup.lockDay;
  const looking = !t.pairId && !t.matchedPayments.length && !t.split && !t.reconciled && !locked;
  const q = useMatches(t.id, looking);
  const pair = usePair();
  const unpair = useUnpair();
  const matchPay = useMatchPayments();
  const unmatch = useUnmatch();
  const payout = usePayout();
  const toast = useToast();
  const run = (what: () => Promise<unknown>, said: string) =>
    void what().then(
      () => {
        toast(said);
        onDone();
      },
      (e: unknown) => toast(errText(e), { kind: "error" }),
    );
  if (t.pairId)
    return (
      <Banner
        type="success"
        action={
          !t.reconciled && !locked ? (
            <Button
              size="small"
              type="tertiary"
              loading={unpair.isPending}
              onClick={() => run(() => unpair.mutateAsync(t.id), "Unpaired")}
            >
              Unpair
            </Button>
          ) : undefined
        }
      >
        Paired with the other side of this transfer. Both sides cancel out, so neither counts in profit.
      </Banner>
    );
  if (t.matchedPayments.length)
    return (
      <Banner
        type="success"
        action={
          !locked ? (
            <Button
              size="small"
              type="tertiary"
              loading={unmatch.isPending}
              onClick={() => run(() => unmatch.mutateAsync(t.id), "Match taken off")}
            >
              Undo match
            </Button>
          ) : undefined
        }
      >
        Matched to {plural(t.matchedPayments.length, "payment")} in the Steward, counted as Sales.
      </Banner>
    );
  if (!looking) return null;
  const m = q.data;
  if (!m || (m.pairs.length === 0 && m.payments.length === 0 && !m.stripe)) return null;
  return (
    <div className="bk-match">
      {m.pairs.length ? (
        <div className="stack tight">
          <b>Is this a transfer between your accounts?</b>
          {m.pairs.map((p) => (
            <div className="bk-match-row" key={p.id}>
              <span className="bk-desc">
                <span>
                  {p.accountName}, {shortDate(p.day)}, {moneyExact(p.amountCents)}
                </span>
                <span className="muted small">
                  {p.description}. Saves as {p.categoryName}
                </span>
              </span>
              <Button
                size="small"
                type="secondary"
                loading={pair.isPending}
                onClick={() => run(() => pair.mutateAsync({ id: t.id, otherId: p.id }), "Paired. Both sides are posted")}
              >
                Pair
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {m.payments.length ? (
        <div className="stack tight">
          <b>Is this the money you took to the bank?</b>
          {m.payments.map((p) => (
            <div className="bk-match-row" key={p.ids.join()}>
              <span className="bk-desc">
                <span>
                  {shortDate(p.day)}, {moneyExact(p.amountCents)}
                </span>
                <span className="muted small">{p.label}</span>
              </span>
              <Button
                size="small"
                type="secondary"
                loading={matchPay.isPending}
                onClick={() => run(() => matchPay.mutateAsync({ id: t.id, paymentIds: p.ids }), "Matched and posted as Sales")}
              >
                Match
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {m.stripe ? (
        <div className="bk-match-row">
          <span className="muted small">A card or bank payout from Stripe? Books splits it into sales and Stripe's fees.</span>
          <Button
            size="small"
            type="tertiary"
            loading={payout.isPending}
            onClick={() => run(() => payout.mutateAsync(t.id), "Found the payout and split it")}
          >
            Find the Stripe payout
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Receipts({ transactionId, companyId }: { transactionId: string; companyId: string }) {
  const q = useReceipts(transactionId);
  const add = useAddReceipt();
  const remove = useRemoveReceipt();
  const toast = useToast();
  const pick = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 10 * 1_048_576) {
      toast("That file is too big. The most is 10 MB.", { kind: "error" });
      return;
    }
    try {
      await add.mutateAsync({
        transactionId,
        name: file.name,
        mime: file.type || "application/octet-stream",
        dataBase64: await base64Of(file),
      });
      toast("Receipt kept");
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  const files = q.data?.files ?? [];
  return (
    <div className="bk-receipts">
      <div className="row between">
        <b>Receipts</b>
        <label className="btn secondary small bk-file-btn" aria-busy={add.isPending}>
          <Icon name="camera" size={14} /> Add a photo or PDF
          <input
            type="file"
            className="sr-only"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={(e) => void pick(e.target.files?.[0])}
          />
        </label>
      </div>
      {files.length === 0 ? (
        <p className="muted small">Keep the receipt here so the accountant and the IRS can see it.</p>
      ) : (
        <ul className="bk-receipt-list">
          {files.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                className="linkish"
                onClick={() => void openReceipt(f.id, companyId).catch((e: unknown) => toast(errText(e), { kind: "error" }))}
              >
                <Icon name="file" size={14} /> {f.name}
              </button>
              <IconButton icon="trash" label={`Remove ${f.name}`} onClick={() => void remove.mutateAsync(f.id)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Bringing transactions in -------------------------------------------------------------------------------------------

function UploadModal({ setup, open, onClose }: { setup: BooksSetup; open: boolean; onClose: () => void }) {
  const upload = useImport();
  const undo = useUndoImport();
  const toast = useToast();
  const bankish = setup.accounts.filter((a) => a.kind !== "cash");
  const [accountId, setAccountId] = useState(bankish[0]?.id ?? "");
  const [file, setFile] = useState<File | null>(null);
  const [flip, setFlip] = useState(false);
  const card = setup.accounts.find((a) => a.id === accountId)?.kind === "card";
  const go = async () => {
    if (!file) return;
    try {
      const text = await file.text();
      const kind = /\.(ofx|qfx|qbo)$/i.test(file.name) || /<OFX>/i.test(text) ? "ofx" : "csv";
      const r = await upload.mutateAsync({ accountId: accountId !== "" ? accountId : (bankish[0]?.id ?? ""), kind, text, flip });
      onClose();
      toast(
        `${String(r.added)} added, ${String(r.skipped)} already in${r.bad ? `, ${String(r.bad)} rows could not be read` : ""}${
          r.locked ? `, ${String(r.locked)} left out because those days are closed` : ""
        }.`,
        { ...(r.importId && r.added ? { undo: () => void undo.mutateAsync(r.importId!) } : {}) },
      );
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Modal
      title="Upload a bank file"
      open={open}
      onClose={onClose}
      footer={
        <Button onClick={() => void go()} loading={upload.isPending} disabled={!file || bankish.length === 0}>
          Upload
        </Button>
      }
    >
      {bankish.length === 0 ? (
        <Banner type="warning">Add a bank or card account first, in Accounts.</Banner>
      ) : (
        <div className="stack">
          <p className="muted">
            Download the account's transactions from the bank's website as CSV, QFX or OFX. Rows already in are skipped, so the
            same days can go in twice.
          </p>
          <Combobox
            label="Account"
            value={accountId || bankish[0]!.id}
            onChange={setAccountId}
            options={bankish.map((a) => ({ value: a.id, label: a.name }))}
          />
          <label className="bk-drop">
            <Icon name="file" size={22} />
            <span>{file ? file.name : "Pick the file the bank gave you"}</span>
            <input
              type="file"
              className="sr-only"
              accept=".csv,.ofx,.qfx,.qbo,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
          {card ? (
            <div className="row tight">
              <Switch checked={flip} onChange={setFlip} label="Charges show as plus" />
              <span>This card's file shows charges as plus amounts</span>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function TypedModal({ setup, open, onClose }: { setup: BooksSetup; open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const typed = useTyped();
  const toast = useToast();
  const cash = setup.accounts.filter((a) => a.kind === "cash");
  const [accountId, setAccountId] = useState(cash[0]?.id ?? "");
  const [day, setDay] = useState(todayIn(company.timezone));
  const [what, setWhat] = useState("");
  const [amount, setAmount] = useState("");
  const [way, setWay] = useState<"out" | "in">("out");
  const [categoryId, setCategoryId] = useState("");
  const cents = centsOf(amount);
  const go = async () => {
    if (cents === null || cents === 0) return;
    try {
      await typed.mutateAsync({
        accountId: accountId !== "" ? accountId : (cash[0]?.id ?? ""),
        day,
        description: what,
        amountCents: way === "out" ? -Math.abs(cents) : Math.abs(cents),
        categoryId: categoryId || null,
      });
      toast("Added");
      setWhat("");
      setAmount("");
      onClose();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Modal
      title="Type in cash"
      open={open}
      onClose={onClose}
      footer={
        <Button onClick={() => void go()} loading={typed.isPending} disabled={!what.trim() || !cents}>
          Add
        </Button>
      }
    >
      <div className="stack">
        <Combobox
          label="Account"
          value={accountId || (cash[0]?.id ?? "")}
          onChange={setAccountId}
          options={cash.map((a) => ({ value: a.id, label: a.name }))}
        />
        <Choice
          label="Money"
          value={way}
          onChange={setWay}
          options={[
            { value: "out", label: "Paid out" },
            { value: "in", label: "Came in" },
          ]}
        />
        <InputText label="Day" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        <InputText label="What for" value={what} maxLength={200} onChange={(e) => setWhat(e.target.value)} />
        <InputText label="Amount" prefix="$" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <Combobox label="Category" value={categoryId} onChange={setCategoryId} options={pickList(setup.categories)} />
      </div>
    </Modal>
  );
}

/** Plaid Link: the owner signs in to the bank inside Plaid's own window; we never see the password. */
export function LinkBank({ setup }: { setup: BooksSetup }) {
  const token = usePlaidLinkToken();
  const connect = usePlaidConnect();
  const { company } = useSession();
  const settings = useSettings(company.id);
  const saveSettings = useUpdateSettings();
  const toast = useToast();
  // Asked first, so a bank that sends 2 years does not fill Books with years already done.
  const [asking, setAsking] = useState(false);
  const [start, setStart] = useState("");
  if (!setup.plaidReady)
    return (
      <Button type="secondary" icon="lock" disabled title="Ask head office to save Plaid's keys">
        Link a bank
      </Button>
    );
  const ask = () => {
    const saved = settings.data?.settings.books.startDay ?? "";
    setStart(saved !== "" ? saved : `${String(new Date().getFullYear())}-01-01`);
    setAsking(true);
  };
  const go = async () => {
    try {
      await saveSettings.mutateAsync({ books: { startDay: start } });
      setAsking(false);
      const { linkToken } = await token.mutateAsync();
      if (__PREVIEW__) {
        toast("In the real app, the bank's sign in opens here.");
        return;
      }
      const plaid = await loadPlaid();
      plaid
        .create({
          token: linkToken,
          onSuccess: (publicToken: string) => {
            void connect.mutateAsync(publicToken).then(
              (r) => toast(`Bank linked. ${String(r.added)} transactions came in.`),
              (e: unknown) => toast(errText(e), { kind: "error" }),
            );
          },
        })
        .open();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <>
      <Button type="secondary" icon="building" loading={token.isPending || connect.isPending} onClick={ask}>
        Link a bank
      </Button>
      {asking ? (
        <Modal
          title="Link a bank"
          open
          onClose={() => setAsking(false)}
          footer={
            <Button
              onClick={() => void go()}
              loading={saveSettings.isPending}
              disabled={start !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(start)}
            >
              Next
            </Button>
          }
        >
          <StartDayPick value={start} onChange={setStart} />
        </Modal>
      ) : null}
    </>
  );
}

/** Plaid's update mode: the owner signs in to the bank again; the link and its transactions stay. */
function FixLink({ connectionId }: { connectionId: string }) {
  const token = usePlaidLinkToken();
  const sync = usePlaidSync();
  const toast = useToast();
  const go = async () => {
    try {
      const { linkToken } = await token.mutateAsync(connectionId);
      if (__PREVIEW__) return;
      (await loadPlaid())
        .create({
          token: linkToken,
          onSuccess: () => {
            void sync.mutateAsync().then(
              (r) => toast(`Fixed. ${String(r.added)} new from the bank.`),
              (e: unknown) => toast(errText(e), { kind: "error" }),
            );
          },
        })
        .open();
    } catch (e) {
      toast(errText(e), { kind: "error" });
    }
  };
  return (
    <Button size="small" loading={token.isPending || sync.isPending} onClick={() => void go()}>
      Fix the link
    </Button>
  );
}

interface PlaidLink {
  create(o: { token: string; onSuccess: (publicToken: string) => void }): { open(): void };
}
function loadPlaid(): Promise<PlaidLink> {
  const w = window as unknown as { Plaid?: PlaidLink };
  if (w.Plaid) return Promise.resolve(w.Plaid);
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
    s.onload = () => (w.Plaid ? resolve(w.Plaid) : reject(new Error("Plaid did not load.")));
    s.onerror = () => reject(new Error("Plaid did not load. Check your connection."));
    document.head.appendChild(s);
  });
}

/** Transactions before there is any account: link the bank (it makes the accounts), or add them by hand and upload files. */
export function NoAccounts({ setup, onAdd }: { setup: BooksSetup; onAdd: () => void }) {
  return (
    <div className="grid-2">
      <Card title="Link your bank">
        <p className="muted">
          Sign in to the bank once and its accounts and transactions come in on their own every day. Read only, and the password
          is never kept here.
        </p>
        {setup.plaidReady ? (
          <LinkBank setup={setup} />
        ) : (
          <Banner type="notice">
            Linking a bank needs Plaid, the service banks use for apps like this. Head office saves Plaid's two keys in Settings,
            Connected services first.
          </Banner>
        )}
      </Card>
      <Card title="Or upload the bank's file">
        <p className="muted">
          Add each bank account and card by hand, then upload the CSV or QFX file from the bank's website. Works with any bank,
          today.
        </p>
        <Button icon="plus" onClick={onAdd}>
          Add an account
        </Button>
      </Card>
    </div>
  );
}
